import Navbar from "../components/Navbar";
import { useState } from "react";
import axios from "axios";
import API from "../config.js";
import { statusToast } from "../utils/notify";

const G = "#FFB800";
const G2 = "#FFD700";
const GD = "#C8860A";

export default function Contact() {
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [status,  setStatusRaw]  = useState(null);
  const setStatus = (s) => { setStatusRaw(s); statusToast(s); };

  const handleSubmit = async () => {
    if (!name || !email || !message) return setStatus({ type: "error", msg: "Please fill all required fields." });
    setLoading(true); setStatus(null);
    try {
      await axios.post(`${API}/api/contact`, { name, email, subject, message });
      setStatus({ type: "success", msg: "Message sent! We'll get back to you within 24 hours." });
      setName(""); setEmail(""); setSubject(""); setMessage("");
    } catch (e) {
      setStatus({ type: "error", msg: e.response?.data?.message || "Failed to send message. Please try again." });
    } finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.container} className="page-container">

        <div style={s.header}>
          <h1 style={s.title}>Contact Us</h1>
          <p style={s.sub}>We're here to help — reach out anytime</p>
        </div>

        <div style={s.grid}>
          {/* Left — Info */}
          <div style={s.infoCol}>
            <InfoCard icon="📧" title="Email Support" value="support@earnixglobal.com" />
            <InfoCard icon="💬" title="Telegram" value="@earnix_global" href="https://t.me/earnix_global" />
            <InfoCard icon="⏰" title="Response Time" value="Within 24 hours" />
            <InfoCard icon="🌐" title="Website" value="earnixglobal.com" />

            <div style={s.note}>
              <div style={s.noteTitle}>⚠ Important</div>
              <div style={s.noteBody}>Earnix Global will never ask for your password, OTP, or private keys. Beware of impersonators.</div>
            </div>
          </div>

          {/* Right — Form */}
          <div style={s.card}>
            <div style={s.cardTopLine} />

            {status && (
              <div style={{
                ...s.alert,
                background: status.type === "success" ? "rgba(0,232,122,0.07)" : "rgba(255,60,87,0.08)",
                border: status.type === "success" ? "1px solid rgba(0,232,122,0.25)" : "1px solid rgba(255,60,87,0.3)",
                color: status.type === "success" ? "#00e87a" : "#ff6b8a",
              }}>
                <span>{status.type === "success" ? "✓" : "⚠"}</span> {status.msg}
              </div>
            )}

            <Field label="Your Name *"    type="text"  placeholder="John Doe"           value={name}    onChange={setName} />
            <Field label="Email Address *" type="email" placeholder="you@example.com"   value={email}   onChange={setEmail} />
            <Field label="Subject"        type="text"  placeholder="How can we help?"   value={subject} onChange={setSubject} />

            <div style={s.fieldWrap}>
              <label style={s.fieldLbl}>Message *</label>
              <textarea
                placeholder="Describe your issue or question..."
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={5}
                style={s.textarea}
              />
            </div>

            <button
              style={{ ...s.btn, opacity: loading ? 0.65 : 1, cursor: loading ? "not-allowed" : "pointer" }}
              onClick={handleSubmit} disabled={loading}>
              {loading ? <span style={s.spinner} /> : <span style={{ letterSpacing: 2 }}>Send Message</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon, title, value, href }) {
  return (
    <div style={ic.card}>
      <span style={ic.icon}>{icon}</span>
      <div>
        <div style={ic.title}>{title}</div>
        {href
          ? <a href={href} target="_blank" rel="noreferrer" style={{...ic.value, color:"#FFD700", textDecoration:"none"}}>{value}</a>
          : <div style={ic.value}>{value}</div>}
      </div>
    </div>
  );
}

function Field({ label, type, placeholder, value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={s.fieldWrap}>
      <label style={s.fieldLbl}>{label}</label>
      <input type={type} placeholder={placeholder} value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ ...s.input, ...(focused ? s.focused : {}) }} />
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", background: "#03050a", color: "#e0e8ff" },
  container: { maxWidth: 1000, margin: "0 auto", padding: "32px 24px" },
  header: { marginBottom: 32 },
  title: {
    fontFamily: "'Figtree', sans-serif", fontWeight: 900, fontSize: 32,
    background: `linear-gradient(135deg, ${G2}, ${G})`,
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    margin: 0, marginBottom: 8,
  },
  sub: { color: "rgba(160,180,220,0.5)", fontSize: 13, margin: 0 },
  grid: { display: "grid", gridTemplateColumns: "300px 1fr", gap: 24, alignItems: "start" },
  infoCol: { display: "flex", flexDirection: "column", gap: 16 },
  card: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,184,0,0.12)",
    borderRadius: 16, padding: "28px",
    position: "relative", overflow: "hidden",
    display: "flex", flexDirection: "column", gap: 18,
  },
  cardTopLine: {
    position: "absolute", top: 0, left: 0, right: 0, height: 2,
    background: `linear-gradient(90deg, transparent, ${G2}, ${G}, transparent)`,
  },
  alert: {
    padding: "10px 14px", borderRadius: 8, fontSize: 13,
    display: "flex", alignItems: "center", gap: 8,
  },
  fieldWrap: { display: "flex", flexDirection: "column", gap: 6 },
  fieldLbl: {
    fontFamily: "'Figtree', sans-serif", fontWeight: 600, fontSize: 11,
    color: "rgba(255,184,0,0.7)", letterSpacing: 1, textTransform: "uppercase",
  },
  input: {
    padding: "12px 14px", fontSize: 14, color: "#f0e6c8",
    background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,184,0,0.12)",
    borderRadius: 8, outline: "none", fontFamily: "'Figtree', sans-serif",
  },
  focused: { border: "1px solid rgba(255,184,0,0.5)", boxShadow: "0 0 0 3px rgba(255,184,0,0.08)" },
  textarea: {
    padding: "12px 14px", fontSize: 14, color: "#f0e6c8",
    background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,184,0,0.12)",
    borderRadius: 8, outline: "none", resize: "vertical",
    fontFamily: "'Figtree', sans-serif", lineHeight: 1.6,
  },
  btn: {
    width: "100%", padding: "14px",
    fontFamily: "'Figtree', sans-serif", fontWeight: 700, fontSize: 13,
    color: "#0a0800",
    background: `linear-gradient(135deg, ${G2}, ${G}, ${GD})`,
    border: "none", borderRadius: 8,
    boxShadow: "0 4px 24px rgba(255,184,0,0.4)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  spinner: {
    display: "inline-block", width: 16, height: 16,
    border: "2px solid rgba(0,0,0,0.2)", borderTop: "2px solid #0a0800",
    borderRadius: "50%", animation: "spin 0.7s linear infinite",
  },
  note: {
    background: "rgba(255,184,0,0.05)", border: "1px solid rgba(255,184,0,0.15)",
    borderRadius: 10, padding: "14px 16px",
  },
  noteTitle: { fontFamily: "'Figtree', sans-serif", fontWeight: 700, fontSize: 13, color: G, marginBottom: 6 },
  noteBody: { fontSize: 12, color: "rgba(160,180,220,0.55)", lineHeight: 1.6 },
};

const ic = {
  card: {
    background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,184,0,0.1)",
    borderRadius: 10, padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 14,
  },
  icon: { fontSize: 24 },
  title: { fontSize: 11, color: "rgba(255,184,0,0.6)", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 },
  value: { fontSize: 13, color: "#e0e8ff", fontWeight: 500 },
};
